import { createFileRoute } from '@tanstack/react-router'

import { BrandAssets, FontLibrarySheet, SETUP_STEPS, WizardStep } from '@/components/onboarding'
import { VENUE_NAME } from '@/mocks/onboarding/podnik'
import { COLORS, EXTRA_FILES, FONT, FONT_LIBRARY, LOGOS } from '@/mocks/onboarding/znacka'

/** How far they got, and whether the font library is open on top of it. */
type ZnackaSearch = { stav?: 'vyplneno'; info?: 'pisma' }

const PAGE_HREF = '/onboarding/znacka'
const LIBRARY_HREF = `${PAGE_HREF}?info=pisma`

export const Route = createFileRoute('/onboarding/znacka')({
  validateSearch: (search: Record<string, unknown>): ZnackaSearch => ({
    ...(search.stav === 'vyplneno' ? { stav: 'vyplneno' as const } : {}),
    ...(search.info === 'pisma' ? { info: 'pisma' as const } : {}),
  }),
  head: () => ({ meta: [{ title: 'Logo a barvy · Aplikace Bitez' }] }),
  component: OnboardingZnacka,
})

/**
 * The second run's first step — everything the client owns of their visual
 * identity, and the rule is **give them somewhere to put all of it**: the
 * palette, logos in the plural (a tile carries its version by colour — tmavá ·
 * světlá · značka — so three files don't look like one file three times), a
 * font, and any other material Bára would otherwise chase in the chat.
 *
 * **Colours come before logos** (owner, 2026-08-20). A venue owner knows their
 * own colours; a logo file is a hunt through old e-mail. Leading with the
 * answerable thing means the screen is never empty, and the logo — when it
 * arrives — only refines what is already there (**the hex under each swatch is
 * the receipt** that we really opened the file).
 *
 * It is first of the four because it unblocks the most: she cannot build a
 * single post without knowing what the venue looks like on paper.
 *
 * The font's three routes are ordered by how often each one is the answer:
 * **the library first**, because most venues do not own a typeface, they choose
 * one — and choosing is something you do by looking (every row sets the venue's
 * own name in that face; seeing "Pizzerie U Pece" in six typefaces is a
 * decision anyone can make in five seconds, reading the word "Poppins" is not).
 * Uploading is second, for the ones whose designer made them a kit, and
 * **typing the name is the last resort**: it is the only route that asks the
 * client to know a piece of trivia.
 */
function OnboardingZnacka() {
  const { stav, info } = Route.useSearch()
  const filled = stav === 'vyplneno'

  return (
    <WizardStep
      step={1}
      steps={SETUP_STEPS}
      backHref="/klient/prehled"
      nextHref="/onboarding/napojeni"
      note="Co nám dáte, použijeme na vaší grafice. Co nedáte, doladí Bára."
      skip={filled ? undefined : { label: 'Nemáme to po ruce', href: '/onboarding/napojeni' }}
      sheet={
        info === 'pisma' && (
          <FontLibrarySheet fonts={FONT_LIBRARY} sample={VENUE_NAME} closeHref={PAGE_HREF} />
        )
      }
    >
      <BrandAssets
        venue={VENUE_NAME}
        logos={filled ? LOGOS : []}
        colors={filled ? COLORS : []}
        font={filled ? FONT : undefined}
        libraryHref={LIBRARY_HREF}
        files={filled ? EXTRA_FILES : []}
      />
    </WizardStep>
  )
}
